import React, { useState } from "react";
import { Button, Text, TabList, Tab, SelectTabEvent, SelectTabData, Input, Field, Spinner } from "@fluentui/react-components";
import { useNavigate } from "react-router-dom";
import { Add16Filled, Archive20Filled, ArchiveArrowBack20Filled, Delete20Filled, Edit20Filled, OrganizationFilled, Person20Filled } from "@fluentui/react-icons";
import { Tenant, useTenants, useUpdateTenantStatus, useDeleteTenant } from "#root/queries/tenants";
import { ROUTES } from "#root/routes/constants";
import { TENANT_STATUS } from "#root/utils/tenants";
import { showToast } from "#root/components/GlobalToaster";
import { AxiosError } from "axios";
import LoadingWrapper from "#root/components/LoadingWrapper";
import Paginator from "#root/components/Paginator";
import { DialogBox } from "#root/components/DialogBox";

const OrganizationsList: React.FC = () => {
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState(TENANT_STATUS.ACTIVE);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleteConfirmDialogOpen, setIsDeleteConfirmDialogOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");

  const { data: tenantsResponse, isFetching, refetch } = useTenants({
    archived: selectedStatus === TENANT_STATUS.ARCHIVED,
    skip: (currentPage - 1) * itemsPerPage,
    take: itemsPerPage
  });

  const totalCount = tenantsResponse?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const tenants = tenantsResponse?.items || [];

  const updateTenantStatusMutation = useUpdateTenantStatus();
  const deleteTenantMutation = useDeleteTenant();

  const handleItemsPerPageChange = (newItemsPerPage: number): void => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    refetch();
  };

  const handleCreate = () => {
    navigate(ROUTES.CREATE_ORGANIZATION);
  };

  const handleEdit = (organization: Tenant) => {
    navigate(`${ROUTES.EDIT_ORGANIZATION}?organizationId=${organization.id}`);
  };

  const handleTabSelect = (event: SelectTabEvent, data: SelectTabData) => {
    const newStatus = data.value as string;
    setSelectedStatus(newStatus);
    setCurrentPage(1);
    refetch();
  };

  const handleLoginAsAdmin = (organization: Tenant) => {
    if (organization.id) {
      localStorage.setItem('tenantId', organization.id);
      window.location.reload();
    } else {
      console.error('Tenant ID is undefined.');
    }
  };

  const handleArchive = (organization: Tenant, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedTenant(organization);
    setIsArchiveDialogOpen(true);
  };

  const handleConfirmArchive = async () => {
    if (selectedTenant) {
      try {
        await updateTenantStatusMutation.mutateAsync({
          id: selectedTenant.id!,
          status: selectedStatus === TENANT_STATUS.ARCHIVED ? TENANT_STATUS.ACTIVE : TENANT_STATUS.ARCHIVED
        });
        setIsArchiveDialogOpen(false);
        refetch();
        showToast(`Organization ${selectedStatus === TENANT_STATUS.ARCHIVED ? 'un-archived' : 'archived'} successfully`, 'success');
      } catch (error) {
        showToast(`Failed to ${selectedStatus === TENANT_STATUS.ARCHIVED ? 'un-archive' : 'archive'} organization`, 'error');
      }
    }
  };

  const handleDelete = (organization: Tenant, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedTenant(organization);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleteDialogOpen(false);
    setIsDeleteConfirmDialogOpen(true);
  };

  const handleConfirmDeleteWithPassword = async () => {
    if (selectedTenant) {
      try {
        await deleteTenantMutation.mutateAsync({
          id: selectedTenant.id!,
          password: deletePassword
        });
        setIsDeleteConfirmDialogOpen(false);
        setDeletePassword("");
        refetch();
        showToast('Organization deleted successfully', 'success');
      }
      catch (error: unknown) {
        const axiosError = error as AxiosError<{ Message: string }>;
        if (axiosError.response?.data?.Message?.includes("Password")) {
          showToast('Incorrect password', 'error');
        }
        else {
          showToast('Failed to delete organization', 'error');
        }
      }
    }
  };

  return (
    <div className="organizations-list-section">
      <h3>List of Organizations
        <TabList className="organizations-tab-list"
          selectedValue={selectedStatus}
          onTabSelect={handleTabSelect}>
          <Tab value={TENANT_STATUS.ACTIVE}>Active Organizations</Tab>
          <Tab value={TENANT_STATUS.ARCHIVED}>Archived Organizations</Tab>
        </TabList>
        <Button
          appearance="primary"
          icon={<Add16Filled />}
          onClick={handleCreate}>
          New Organization
        </Button>
      </h3>

      <div className="list-cards">
        <LoadingWrapper
          isLoading={isFetching}
          isEmpty={tenants.length === 0}
          emptyMessage={`No ${selectedStatus.toLowerCase()} organizations found`}
          emptyIcon={<OrganizationFilled />}
        >
          {tenants.map((tenant) => (
            <div key={tenant.id} className="list-card">
              <div>
                <Text weight="semibold" truncate>{tenant.organizationName}</Text>
              </div>
              <div>
                <Text weight="semibold" truncate>{tenant.organizationId}</Text>
              </div>
              <div className="list-card__actions">
                {(() => {
                  switch (tenant.status) {
                    case TENANT_STATUS.CREATION_IN_PROGRESS:
                      return (
                        <div className="creation-in-progress">
                          <Spinner size="tiny" />
                          <Text weight="semibold">Creation in progress</Text>
                        </div>
                      );
                    case TENANT_STATUS.ACTIVE:
                      return (
                        <>
                          <Button
                            className="expanding-action-button"
                            appearance="primary"
                            icon={<Person20Filled />}
                            onClick={() => handleLoginAsAdmin(tenant)}
                          >
                            <span className="button-text">Login as Administrator</span>
                          </Button>
                          <Button
                            className="expanding-action-button"
                            appearance="transparent"
                            icon={<Edit20Filled />}
                            onClick={() => handleEdit(tenant)}
                          >
                            <span className="button-text">Edit</span>
                          </Button>
                          <Button
                            className="expanding-action-button"
                            appearance="transparent"
                            icon={<Archive20Filled />}
                            onClick={(e) => handleArchive(tenant, e)}
                          >
                            <span className="button-text">Archive</span>
                          </Button>
                        </>
                      );
                    case TENANT_STATUS.ARCHIVED:
                      return (
                        <>
                          <Button
                            className="expanding-action-button"
                            appearance="transparent"
                            icon={<ArchiveArrowBack20Filled />}
                            onClick={(e) => handleArchive(tenant, e)}
                          >
                            <span className="button-text">Unarchive</span>
                          </Button>
                          <Button
                            className="expanding-action-button"
                            appearance="transparent"
                            icon={<Delete20Filled />}
                            onClick={(e) => handleDelete(tenant, e)}
                          >
                            <span className="button-text">Delete</span>
                          </Button>
                        </>
                      );
                    default:
                      return null;
                  }
                })()}
              </div>
            </div>
          ))}
          <Paginator
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={totalCount}
            currentItems={(currentPage - 1) * itemsPerPage + 1}
            showItemsPerPage={true}
          />
        </LoadingWrapper>
      </div>

      <DialogBox
        open={isArchiveDialogOpen}
        onOpenChange={setIsArchiveDialogOpen}
        title={`${selectedStatus === TENANT_STATUS.ARCHIVED ? 'Un-archive' : 'Archive'} Organization`}
        primaryButtonText="Confirm"
        primaryButtonAppearance="primary"
        onPrimaryButtonClick={handleConfirmArchive}
        isPrimaryButtonLoading={updateTenantStatusMutation.isPending}
      >
        Are you sure you want to {selectedStatus === TENANT_STATUS.ARCHIVED ? 'un-archive' : 'archive'} {selectedTenant?.organizationName}?
      </DialogBox>
      <DialogBox
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title="Delete Organization"
        primaryButtonText="Confirm"
        primaryButtonAppearance="primary"
        onPrimaryButtonClick={handleConfirmDelete}
      >
        Are you sure you want to delete {selectedTenant?.organizationName}? This process is non reversible.
      </DialogBox>
      <DialogBox
        open={isDeleteConfirmDialogOpen}
        onOpenChange={setIsDeleteConfirmDialogOpen}
        title="Confirm Delete Organization"
        primaryButtonText="Delete"
        primaryButtonAppearance="primary"
        onPrimaryButtonClick={handleConfirmDeleteWithPassword}
        isPrimaryButtonLoading={deleteTenantMutation.isPending}
        isPrimaryButtonDisabled={!deletePassword}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Text weight="semibold">Enter your password below to confirm.</Text>
          <Field label="Password" required>
            <Input
              required
              value={deletePassword}
              onChange={(_, data) => setDeletePassword(data.value)}
            />
          </Field>
        </div>
      </DialogBox>
    </div>
  );
};

export default OrganizationsList;
